import { storePagination } from '@/mixins/store'
import { sorting } from '@/mixins'
import axios from '@axios'
import fiscalTaxParameterMaintain from './fiscal-tax-parameter-maintain'

const getInitialState = () => ({
  ...storePagination.state(),
  fiscalTaxParameters: [],
  sorting: {
    sortBy: 'ncm',
    sortDesc: false,
  },
  filters: {
    ncm: null,
    active: null,
    stateIdIbge: null,
    cityIdIbge: null,
    taxEnabledSelectiveTax: null,
  },
  combos: {
    cstCbsIbs: [],
    states: [],
    citiesByState: {},
    selectiveTaxTypes: [],
    classTribsByCst: {},
  },
})

export default {
  namespaced: true,
  state: getInitialState(),
  modules: { fiscalTaxParameterMaintain },

  getters: {
    getCstCbsIbs: state => state.combos.cstCbsIbs || [],
    getSelectiveTaxTypes: state => state.combos.selectiveTaxTypes || [],
    getStates: state => state.combos.states || [],
    getCitiesByState: state => id => state.combos.citiesByState[id] || [],
    getClassTribsByCst: state => cst => state.combos.classTribsByCst[cst] || [],
  },

  mutations: {
    ...storePagination.mutations,

    SET_FISCAL_TAX_PARAMETERS(state, fiscalTaxParameters) {
      state.fiscalTaxParameters = fiscalTaxParameters
    },

    SET_FILTER(state, filters) {
      state.filters = filters
    },

    SET_COMBO(state, { name, data }) {
      state.combos = { ...state.combos, [name]: data }
    },
    SET_CITIES_BY_STATE(state, { stateId, data }) {
      state.combos.citiesByState = { ...state.combos.citiesByState, [stateId]: data }
    },
    SET_CLASS_TRIBS_BY_CST(state, { cst, data }) {
      state.combos.classTribsByCst = { ...state.combos.classTribsByCst, [cst]: data }
    },

    CLEAN_STATE(state) {
      const defaultData = getInitialState()
      Object.keys(defaultData).forEach(key => {
        state[key] = defaultData[key]
      })
    },
  },

  actions: {
    ...storePagination.actions,

    async fetchFiscalTaxParameters({ commit, state }) {
      const response = await axios.get('/api/tax/fiscal-tax-parameters', {
        params: {
          ncm: state.filters.ncm || undefined,
          active: state.filters.active,
          cityIdIbge: state.filters.cityIdIbge,
          stateIdIbge: state.filters.stateIdIbge,
          taxEnabledSelectiveTax: state.filters.taxEnabledSelectiveTax,
          sortBy: sorting.methods.getSorting(state),
          pageSize: state.paging.pageSize,
          pageIndex: state.paging.currentPage - 1,
        },
      })
      commit('SET_FISCAL_TAX_PARAMETERS', response.data.results)
      commit('SET_PAGING', {
        ...state.paging,
        rowCount: response.data.rowCount,
        rowsInCurrentPage: response.data.rowsInCurrentPage,
      })
    },

    async fetchCombos({ state, commit }) {
      if (state.combos.cstCbsIbs.length === 0) {
        const { data } = await axios.get('/api/tax/tax-reform/csts')
        commit('SET_COMBO', { name: 'cstCbsIbs', data: data || [] })
      }

      if (state.combos.selectiveTaxTypes.length === 0) {
        commit('SET_COMBO', {
          name: 'selectiveTaxTypes',
          data: [
            { value: 'PERCENTAGE_BY_BASE_VALUE', label: 'Porcentagem por Base de Cálculo' },
            { value: 'PERCENTAGE_BY_UNIT', label: 'Porcentagem por Unidade' },
          ],
        })
      }
    },

    async fetchStates({ state, commit }) {
      if (state.combos.states.length === 0) {
        const { data } = await axios.get('/api/states', { params: { pageSize: 0, pageIndex: 0 } })
        commit('SET_COMBO', { name: 'states', data: data.results || [] })
      }
    },

    async fetchCitiesByState({ commit, state }, stateIdIbge) {
      if (!stateIdIbge) return
      if (state.combos.citiesByState[stateIdIbge]) return
      const { data } = await axios.get('/api/cities', {
        params: { pageSize: 0, pageIndex: 0, stateIdIbge },
      })
      commit('SET_CITIES_BY_STATE', { stateId: stateIdIbge, data: data.results || [] })
    },

    async fetchClassTribsByCst({ state, commit }, cst) {
      if (!cst) return []
      if (state.combos.classTribsByCst[cst]) return state.combos.classTribsByCst[cst]
      const { data } = await axios.get('/api/tax/tax-reform/class-trib', { params: { cst } })
      commit('SET_CLASS_TRIBS_BY_CST', { cst, data: data || [] })
      return data || []
    },

    async generateReport({ state }) {
      const {
        ncm,
        active,
        cityIdIbge,
        stateIdIbge,
        taxEnabledSelectiveTax,
      } = state.filters

      const params = {
        ncm,
        active,
        cityIdIbge,
        stateIdIbge,
        taxEnabledSelectiveTax,
        sortBy: 'CreatedDate ASC',
        pageSize: 0,
        pageIndex: 0,
      }

      const { data } = await axios.get('/api/tax/fiscal-tax-parameters/download', {
        params,
        responseType: 'blob',
      })
      return data
    },

    setFilter({ commit }, filter) {
      commit('SET_CURRENT_PAGE', 1)
      commit('SET_FILTER', filter)
    },

    resetFilters({ commit }) {
      commit('SET_CURRENT_PAGE', 1)
      commit('SET_FILTER', getInitialState().filters)
    },

    cleanState({ commit }) {
      commit('CLEAN_STATE')
    },
  },
}
